import { useEffect, useRef } from 'react'

import { useActivity } from '@/api/hooks/useCredits'
import { getModule } from '@/modules/registry'
import { formatRelative } from '@/shared/lib/format'
import { Button } from '@/shared/ui/Button'
import { Skeleton } from '@/shared/ui/Card'

export interface ActivityPanelProps {
  open: boolean
  onClose: () => void
}

/**
 * Registro attività.
 *
 * Un pannello laterale con quello che è successo, in ordine. Serve a
 * rispondere alla domanda "ma cosa ho appena fatto?" senza dover ricostruire
 * a memoria — e a rendere leggibile ciò che i moduli fanno per conto tuo.
 */
export function ActivityPanel({ open, onClose }: ActivityPanelProps) {
  const { data, isLoading } = useActivity()
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    closeRef.current?.focus()

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  if (!open) return null

  return (
    <aside
      id="pannello-attivita"
      aria-label="Registro attività"
      className="border-l border-[var(--hairline)] bg-ink-900 flex w-80 shrink-0 flex-col"
    >
      <div className="flex items-center justify-between border-b border-[var(--hairline)] px-4 py-3">
        <h2 className="type-eyebrow">Attività</h2>
        <Button
          ref={closeRef}
          variant="quiet"
          size="sm"
          onClick={onClose}
          aria-label="Chiudi il registro attività"
        >
          ✕
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col gap-2 p-4">
            <Skeleton className="h-12 w-full" label="Caricamento del registro" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : !data || data.length === 0 ? (
          <p className="text-thread-500 text-sm p-4">
            Ancora nessuna attività. Comincia da una ricerca.
          </p>
        ) : (
          <ul>
            {data.map((entry) => (
              <li key={entry.id} className="border-b border-[var(--hairline)] px-4 py-3">
                <div className="type-code text-thread-500 mb-1 flex justify-between gap-2">
                  <span>{getModule(entry.module).name}</span>
                  <time dateTime={entry.createdAt}>{formatRelative(entry.createdAt)}</time>
                </div>
                <p className="text-linen-200 text-sm">{entry.message}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  )
}
